import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthedImage from "@/components/AuthedImage";
import SocialLinks from "@/components/SocialLinks";
import HScroll from "@/components/HScroll";
import { Inbox, Share2, Camera, Copy, Check, Users, UserPlus, MessageCircle, Mail } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPreview, setPendingPreview] = useState([]);
  const [invite, setInvite] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [copied, setCopied] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [peopleSections, setPeopleSections] = useState([]);
  const [conversions, setConversions] = useState({ count: 0, recent: [] });

  const reload = useCallback(async () => {
    const [a, p, i, ppl, conv] = await Promise.all([
      api.get("/albums"),
      api.get("/photos/pending"),
      api.get("/invites/primary"),
      api.get("/people-like-me").catch(() => ({ data: { sections: [] } })),
      api.get("/invites/conversions").catch(() => ({ data: { count: 0, recent: [] } })),
    ]);
    setAlbums(a.data.albums);
    setPendingCount(p.data.count);
    setPendingPreview(p.data.photos.slice(0, 4));
    setInvite(i.data);
    setPeopleSections(ppl.data.sections || []);
    setConversions(conv.data || { count: 0, recent: [] });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const createAlbum = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    await api.post("/albums", { title: newTitle, is_public: false });
    setNewTitle("");
    reload();
  };

  const inviteLink = (code) =>
    `${window.location.origin}/invite/${code}`;

  const joinLink = `${window.location.origin}/api/share/welcome/${user?.screen_name || ""}`;
  const ogPreviewUrl = `${window.location.origin}/api/og/welcome/${user?.screen_name || ""}.png`;

  const shareMsg = `Join me on MemoryPeaks — friends can share photos of you, and you decide what to keep. Create your free account: ${joinLink}`;

  const socialPost = `📸 Check out MemoryPeaks — a new site where your friends can post photos of YOU that you'd never have otherwise.

You review every photo before anything goes public, organize them into albums, and stay in full control of your privacy.

Come claim your name and start collecting your memories: ${joinLink}`;
  const shareUrls = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareMsg)}`,
    email: `mailto:?subject=${encodeURIComponent("You're invited to MemoryPeaks")}&body=${encodeURIComponent(shareMsg)}`,
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title: "Join me on MemoryPeaks", text: shareMsg, url: joinLink });
    } catch { /* user cancelled */ }
    setShowShare(false);
  };

  const copy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="paper-grain min-h-screen" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <p className="font-script text-2xl text-[#3A5A40]">welcome back, {user?.screen_name}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl mb-1">My Memories</h1>
        <p className="text-[#5C5C5C] mb-6">Your dashboard for albums, pending photos, and invites.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-start">
          {/* Left: main column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Pending */}
          <Link to="/pending" className="paper-card p-5 block hover:-translate-y-1 transition-transform" data-testid="card-pending">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-[#8C3A3A]"/>
                <span className="text-xs uppercase tracking-[0.18em] font-semibold">Pending</span>
              </div>
              <span className="font-serif-display text-3xl">{pendingCount}</span>
            </div>
            <p className="text-sm text-[#5C5C5C] mb-4">Photos others have uploaded of you, waiting for your review.</p>
            <div className="flex gap-1">
              {pendingPreview.length === 0 && <div className="text-xs text-[#8A8A8A] italic">No new photos yet.</div>}
              {pendingPreview.map((p) => (
                <div key={p.id} className="w-12 h-12 overflow-hidden border border-[#E5E0D8]">
                  <AuthedImage photoId={p.id} className="w-full h-full object-cover"/>
                </div>
              ))}
            </div>
          </Link>

          {/* Quick post */}
          <Link to="/post" className="paper-card p-5 block hover:-translate-y-1 transition-transform" data-testid="card-post-photo">
            <div className="flex items-center gap-2 mb-3">
              <Camera className="w-5 h-5 text-[#8C3A3A]"/>
              <span className="text-xs uppercase tracking-[0.18em] font-semibold">Post a photo</span>
            </div>
            <p className="text-sm text-[#5C5C5C] mb-4">Have a picture of a friend? Share it to their album.</p>
            <span className="btn-primary inline-flex">Post photo →</span>
          </Link>
            </div>

            {/* People you might know */}
            {peopleSections.length > 0 && (
              <section data-testid="people-like-me-section">
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <p className="font-script text-2xl text-[#3A5A40]">faces you might know</p>
                    <h2 className="font-serif-display text-3xl flex items-center gap-2">
                      <Users className="w-7 h-7 text-[#3A5A40]"/> People like you
                    </h2>
                  </div>
                  <Link to="/search" className="btn-ghost text-sm" data-testid="people-explore-link">Explore more →</Link>
                </div>
                <div className="space-y-6">
                  {peopleSections.map((sec) => (
                    <div key={sec.field} data-testid={`people-section-${sec.field}`}>
                      <div className="flex items-baseline gap-3 mb-3">
                        <h3 className="font-serif-display text-xl">{sec.label}</h3>
                        <span className="text-sm text-[#8A8A8A] italic">"{sec.value}"</span>
                        <span className="text-xs text-[#8A8A8A]">· {sec.count}</span>
                      </div>
                      {sec.users.length === 0 ? (
                        <p className="text-sm text-[#8A8A8A] italic">
                          No one else has made that field public yet.{" "}
                          <Link to={`/u/${user.screen_name}`} className="underline">
                            Make yours public
                          </Link>{" "}
                          to start showing up here for others.
                        </p>
                      ) : (
                        <HScroll testId={`people-carousel-${sec.field}`}>
                          {sec.users.map((p) => (
                            <div
                              key={p.id}
                              className="paper-card p-4 relative hover:-translate-y-1 transition-transform w-44 shrink-0 snap-start"
                              data-testid={`people-result-${p.screen_name}`}
                            >
                              <Link to={`/u/${p.screen_name}`} className="absolute inset-0 z-0" aria-label={`View @${p.screen_name}`}/>
                              <div className="relative z-[1] pointer-events-none">
                                <p className="font-serif-display text-lg truncate">@{p.screen_name}</p>
                                {p.name && <p className="text-xs text-[#5C5C5C] truncate">{p.name}</p>}
                                {(p.city || p.state) && (
                                  <p className="text-xs text-[#8A8A8A] truncate mt-1">
                                    {[p.city, p.state].filter(Boolean).join(", ")}
                                  </p>
                                )}
                              </div>
                              <SocialLinks socials={p.socials} size="sm" label="" className="relative z-[2] mt-2" testId={`people-socials-${p.screen_name}`}/>
                            </div>
                          ))}
                        </HScroll>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Invite sidebar */}
          <div className="lg:col-span-1">
          {/* Invite */}
          <div className="paper-card p-5" data-testid="card-invites">
            <div className="flex items-center gap-2 mb-3">
              <Share2 className="w-5 h-5 text-[#3A5A40]"/>
              <span className="text-xs uppercase tracking-[0.18em] font-semibold">Invite friends</span>
            </div>
            <p className="text-sm text-[#5C5C5C] mb-4">Share this link so friends can post pictures of you. It always goes to your pending review.</p>
            {invite ? (
              <div className="flex items-center gap-2 text-xs" data-testid="invite-link-row">
                <code className="bg-[#F0EBE1] px-2 py-2 truncate flex-1" data-testid="invite-link-code">{inviteLink(invite.code)}</code>
                <button onClick={() => copy(inviteLink(invite.code), "invite")} className="btn-secondary shrink-0 px-3 py-2" data-testid="copy-invite-btn">
                  {copied === "invite" ? <><Check className="w-4 h-4 text-[#3A5A40]"/> Copied</> : <><Copy className="w-4 h-4"/> Copy</>}
                </button>
              </div>
            ) : (
              <p className="text-xs text-[#8A8A8A] italic">Preparing your invite link…</p>
            )}
            <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
              <p className="text-xs text-[#5C5C5C] mb-2">
                Know someone who'd love MemoryPeaks? Invite them to <strong>create their own account</strong>.
              </p>
              {conversions.count > 0 && (
                <div className="mb-3 flex items-start gap-2 bg-[#F0EBE1] px-3 py-2" data-testid="invite-conversions">
                  <UserPlus className="w-4 h-4 text-[#3A5A40] mt-0.5 shrink-0"/>
                  <div>
                    <p className="text-sm font-semibold text-[#3A5A40]">
                      {conversions.count} friend{conversions.count === 1 ? "" : "s"} joined via your link
                    </p>
                    {conversions.recent.length > 0 && (
                      <p className="text-xs text-[#5C5C5C] truncate">
                        {conversions.recent.slice(0, 3).map((r) => `@${r.screen_name}`).join(", ")}
                        {conversions.count > 3 ? ` +${conversions.count - 3} more` : ""}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="mb-3 bg-[#F9F6F0] border border-[#E5E0D8] p-3" data-testid="social-post-block">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A5A40] mb-2">Post it on social media</p>
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1">How your link looks when shared</p>
                  <img
                    src={ogPreviewUrl}
                    alt="Your shareable invite preview"
                    loading="lazy"
                    className="w-full rounded border border-[#E5E0D8]"
                    data-testid="share-card-preview"
                  />
                </div>
                <p className="text-xs text-[#5C5C5C] leading-relaxed whitespace-pre-line break-words" data-testid="social-post-text">{socialPost}</p>
                <button
                  onClick={() => copy(socialPost, "post")}
                  className="btn-secondary text-sm w-full mt-3 flex items-center justify-center gap-2"
                  data-testid="copy-social-post-btn"
                >
                  {copied === "post" ? <><Check className="w-4 h-4 text-[#3A5A40]"/> Copied — paste it anywhere!</> : <><Copy className="w-4 h-4"/> Copy shareable post</>}
                </button>
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowShare((s) => !s)}
                  className="btn-ghost text-sm w-full flex items-center justify-center gap-2"
                  data-testid="invite-to-join-share-btn"
                >
                  <UserPlus className="w-4 h-4"/> Invite someone to join
                </button>
                {showShare && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowShare(false)} data-testid="share-backdrop"/>
                    <div
                      className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-[#E5E0D8] shadow-lg p-2 space-y-1"
                      data-testid="invite-share-menu"
                    >
                    {navigator.share && (
                      <button onClick={nativeShare} className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#F9F6F0]" data-testid="share-native">
                        <Share2 className="w-4 h-4 text-[#3A5A40]"/> Share via…
                      </button>
                    )}
                    <a href={shareUrls.whatsapp} target="_blank" rel="noreferrer" onClick={() => setShowShare(false)} className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#F9F6F0]" data-testid="share-whatsapp">
                      <MessageCircle className="w-4 h-4 text-[#25D366]"/> WhatsApp
                    </a>
                    <a href={shareUrls.email} onClick={() => setShowShare(false)} className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#F9F6F0]" data-testid="share-email">
                      <Mail className="w-4 h-4 text-[#8C3A3A]"/> Email
                    </a>
                    <button onClick={() => { copy(joinLink, "join"); setShowShare(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#F9F6F0] border-t border-[#E5E0D8]" data-testid="share-copy-link">
                      {copied === "join" ? <><Check className="w-4 h-4 text-[#3A5A40]"/> Copied!</> : <><Copy className="w-4 h-4"/> Copy link</>}
                    </button>
                  </div>
                  </>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Albums */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-script text-2xl text-[#8C3A3A]">your collections</p>
            <h2 className="font-serif-display text-3xl">Albums</h2>
          </div>
          <form onSubmit={createAlbum} className="flex gap-2">
            <input data-testid="new-album-input" placeholder="New album title..." className="mp-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}/>
            <button type="submit" className="btn-primary" data-testid="create-album-btn">Create</button>
          </form>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {albums.map((a) => (
            <Link key={a.id} to={`/album/${a.id}`} className="paper-card p-4 block hover:-translate-y-1 transition-transform" data-testid={`album-card-${a.id}`}>
              <div className="aspect-square bg-[#F0EBE1] mb-4 flex items-center justify-center text-[#8A8A8A] overflow-hidden">
                {a.cover_photo_id ? (
                  <AuthedImage photoId={a.cover_photo_id} className="w-full h-full object-cover"/>
                ) : (
                  <Camera className="w-12 h-12 opacity-30"/>
                )}
              </div>
              <h3 className="font-serif-display text-xl mb-1 truncate">{a.title}</h3>
              <div className="flex items-center justify-between text-xs text-[#5C5C5C]">
                <span>{a.photo_count} photo{a.photo_count === 1 ? "" : "s"}</span>
                <span className={`px-2 py-0.5 ${a.is_public ? "bg-[#3A5A40] text-white" : "bg-[#F0EBE1]"}`}>
                  {a.is_public ? "Public" : "Private"}
                </span>
              </div>
            </Link>
          ))}
          {albums.length === 0 && (
            <p className="text-[#8A8A8A] italic col-span-full">No albums yet. Create one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}
